import cronstrue from "cronstrue/i18n";
import i18next from "i18next";

/**
 * Validates a cron expression format (6 fields with seconds)
 * Format: second minute hour day-of-month month day-of-week
 * @param cronExpression - The cron expression to validate
 * @returns true if valid, false otherwise
 */
export function validateCronExpression(cronExpression: string): boolean {
  if (!cronExpression || typeof cronExpression !== "string") {
    return false;
  }

  const trimmed = cronExpression.trim();
  const fields = trimmed.split(/\s+/);

  if (fields.length !== 6) {
    return false;
  }

  const patterns = [
    /^(\*|([0-5]?\d)(,([0-5]?\d))*|([0-5]?\d)-([0-5]?\d)(\/\d+)?|\*\/\d+)$/, // seconds (0-59)
    /^(\*|([0-5]?\d)(,([0-5]?\d))*|([0-5]?\d)-([0-5]?\d)(\/\d+)?|\*\/\d+)$/, // minutes (0-59)
    /^(\*|(1?\d|2[0-3])(,(1?\d|2[0-3]))*|(1?\d|2[0-3])-(1?\d|2[0-3])(\/\d+)?|\*\/\d+)$/, // hours (0-23)
    /^(\*|\?|([1-9]|[12]\d|3[01])(,([1-9]|[12]\d|3[01]))*|([1-9]|[12]\d|3[01])-([1-9]|[12]\d|3[01])(\/\d+)?|\*\/\d+|L|LW|[1-9]W)$/, // day-of-month (1-31)
    /^(\*|([1-9]|1[0-2])(,([1-9]|1[0-2]))*|([1-9]|1[0-2])-([1-9]|1[0-2])(\/\d+)?|\*\/\d+|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)$/i, // month (1-12 or names)
    /^(\*|\?|([0-7])(,([0-7]))*|([0-7])-([0-7])(\/\d+)?|\*\/\d+|SUN|MON|TUE|WED|THU|FRI|SAT|[0-7]L|[0-7]#[1-5])$/i, // day-of-week (0-7 or names)
  ];

  for (let i = 0; i < fields.length; i++) {
    if (!patterns[i].test(fields[i])) {
      return false;
    }
  }

  return true;
}

/**
 * Converts a 6-field cron expression to human-readable German text
 * Format: second minute hour day-of-month month day-of-week
 * @param cronExpression - The cron expression to convert
 * @returns Human-readable description in German
 */
export function cronToHuman(cronExpression: string): string {
  if (!cronExpression) {
    return i18next.t("common:InvalidCronExpression");
  }

  try {
    const description = cronstrue.toString(cronExpression, {
      locale: i18next.language,
      use24HourTimeFormat: true,
      throwExceptionOnParseError: true,
    });
    return description;
  } catch {
    return i18next.t("common:InvalidCronExpression");
  }
}

/**
 * Converts a 6-field cron expression to human-readable English text (for debugging)
 * @param cronExpression - The cron expression to convert
 * @returns Human-readable description in English
 */
export function cronToHumanEnglish(cronExpression: string): string {
  if (!cronExpression) {
    return i18next.t("common:InvalidCronExpression");
  }

  try {
    const description = cronstrue.toString(cronExpression, {
      use24HourTimeFormat: true,
      throwExceptionOnParseError: true,
    });
    return description;
  } catch {
    return i18next.t("common:InvalidCronExpression");
  }
}

/**
 * Predefined common cron expressions (6-field format with seconds)
 */
export const CRON_PRESETS = {
  EVERY_MINUTE: {
    expression: "0 * * * * *",
    labelKey: "EveryMinute",
  },
  EVERY_5_MINUTES: {
    expression: "0 */5 * * * *",
    labelKey: "Every5Minutes",
  },
  EVERY_15_MINUTES: {
    expression: "0 */15 * * * *",
    labelKey: "Every15Minutes",
  },
  EVERY_30_MINUTES: {
    expression: "0 */30 * * * *",
    labelKey: "Every30Minutes",
  },
  HOURLY: {
    expression: "0 0 * * * *",
    labelKey: "Hourly",
  },
  DAILY_MIDNIGHT: {
    expression: "0 0 0 * * *",
    labelKey: "DailyAtMidnight",
  },
  DAILY_2AM: {
    expression: "0 0 2 * * *",
    labelKey: "DailyAt2AM",
  },
  DAILY_6AM: {
    expression: "0 0 6 * * *",
    labelKey: "DailyAt6AM",
  },
  DAILY_NOON: {
    expression: "0 0 12 * * *",
    labelKey: "DailyAt12PM",
  },
  WEEKLY_MONDAY_2AM: {
    expression: "0 0 2 * * 1",
    labelKey: "WeeklyMonday2AM",
  },
  WEEKLY_SUNDAY_2AM: {
    expression: "0 0 2 * * 0",
    labelKey: "WeeklySunday2AM",
  },
  MONTHLY_FIRST_DAY_2AM: {
    expression: "0 0 2 1 * *",
    labelKey: "MonthlyFirstDay2AM",
  },
  WORKDAYS_6AM: {
    expression: "0 0 6 * * 1-5",
    labelKey: "WeekdaysAt6AM",
  },
  WORKDAYS_8AM: {
    expression: "0 0 8 * * 1-5",
    labelKey: "WeekdaysAt8AM",
  },
} as const;

/**
 * Get all preset options as an array
 */
export function getCronPresets(): Array<{ value: string; label: string; description: string }> {
  return Object.values(CRON_PRESETS).map((preset) => ({
    value: preset.expression,
    label: i18next.t(`common:${preset.labelKey}`),
    description: cronToHuman(preset.expression),
  }));
}
